// Main editor for a single event.
//
// The data layer (Supabase calls through src/lib/store.js and rule evaluation
// through src/lib/rules.js) is unchanged from the original implementation.
// Only the visual layer, component structure, icon set, and copy are new.

import { useState, useRef, useMemo, useEffect } from "react";
import {
  loadEvent,
  addPeople,
  deletePerson,
  addActivity as dbAddActivity,
  updateActivity,
  deleteActivity as dbDeleteActivity,
  addGroup as dbAddGroup,
  deleteGroup as dbDeleteGroup,
  updateGroup,
  setGroupMembers,
  listRules,
  createRule,
  deleteRule,
} from "./lib/store.js";
import { evalRules, proposeSwaps, applyPlacement, pickGroupForPlacement } from "./lib/rules.js";

import { I } from "./components/Icons.jsx";
import Btn from "./components/ui/Btn.jsx";
import IconBtn from "./components/ui/IconBtn.jsx";
import Display from "./components/ui/Display.jsx";
import Eyebrow from "./components/ui/Eyebrow.jsx";
import Toggle from "./components/ui/Toggle.jsx";
import { inputStyle } from "./components/ui/styles.js";

import GroupCard from "./components/editor/GroupCard.jsx";
import { GuestPoolTop, GuestPoolSide } from "./components/editor/GuestPool.jsx";
import SwapOverlay from "./components/editor/SwapOverlay.jsx";
import RulesOverlay from "./components/editor/RulesOverlay.jsx";
import RebalanceOverlay from "./components/editor/RebalanceOverlay.jsx";
import SeatingModal from "./components/editor/SeatingModal.jsx";
import ExportView from "./components/editor/ExportView.jsx";

import SettingsPopover from "./components/SettingsPopover.jsx";
import UserMenu from "./components/UserMenu.jsx";

// Activity type metadata. Icons come from Icons.jsx; all colour comes from tokens.
export const GTYPES = [
  { value: "dinner",     label: "Dinner",     groupLabel: "Table",   Icon: I.dinner },
  { value: "lunch",      label: "Lunch",      groupLabel: "Table",   Icon: I.lunch },
  { value: "discussion", label: "Discussion", groupLabel: "Salon",   Icon: I.discussion },
  { value: "transport",  label: "Transport",  groupLabel: "Vehicle", Icon: I.transport },
  { value: "workshop",   label: "Workshop",   groupLabel: "Team",    Icon: I.workshop },
  { value: "game",       label: "Game",       groupLabel: "Table",   Icon: I.game },
  { value: "social",     label: "Social",     groupLabel: "Circle",  Icon: I.social },
  { value: "excursion",  label: "Excursion",  groupLabel: "Party",   Icon: I.excursion },
  { value: "other",      label: "Other",      groupLabel: "Group",   Icon: I.other },
];
const typeOf = (v) => GTYPES.find(t => t.value === v) || GTYPES[8];

export default function EventApp({
  eventId,
  eventName,
  onBack,
  theme,
  setTheme,
  navMode,
  setNavMode,
  poolPosition,
  setPoolPosition,
  session,
}) {
  // ----- data -----
  const [people, setPeople] = useState([]);
  const [activities, setActivities] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // ----- ui state -----
  const [activeA, setActiveA] = useState(null);
  const [selP, setSelP] = useState(null);
  const [sideOpen, setSideOpen] = useState(false);
  const [dragPid, setDragPid] = useState(null);
  const [swapSrc, setSwapSrc] = useState(null);
  const [showHosts, setShowHosts] = useState(true);
  const [search, setSearch] = useState("");
  const [addingPerson, setAddingPerson] = useState(false);
  const [newPerson, setNewPerson] = useState("");
  const [showNewA, setShowNewA] = useState(false);
  const [naName, setNaName] = useState("");
  const [naType, setNaType] = useState("dinner");
  const [naMaxSize, setNaMaxSize] = useState("");
  const [showNewGr, setShowNewGr] = useState(false);
  const [ngrName, setNgrName] = useState("");
  const [ngrCap, setNgrCap] = useState("");
  const [showRules, setShowRules] = useState(false);
  const [rebalanceProposals, setRebalanceProposals] = useState(null);
  const [proposalBusy, setProposalBusy] = useState(false);
  const [exportMode, setExportMode] = useState(null); // null | "all" | activityId
  const [seatingFor, setSeatingFor] = useState(null); // null | { activityId, groupId }
  const [settingsOpen, setSettingsOpen] = useState(false);
  const csvRef = useRef(null);
  const settingsBtnRef = useRef(null);

  // ----- load -----
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([loadEvent(eventId), listRules(eventId)])
      .then(([d, rs]) => {
        if (cancelled) return;
        setPeople(d.people);
        setActivities(d.activities);
        setRules(rs);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(e.message || String(e));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [eventId]);

  useEffect(() => {
    if (!loading && !activeA && activities.length > 0) setActiveA(activities[0].id);
  }, [loading, activeA, activities]);

  // ----- derived -----
  const cur = activities.find(a => a.id === activeA);
  const ti = cur ? typeOf(cur.type) : null;

  const assignedSet = useMemo(() => {
    const s = new Set();
    cur?.groups.forEach(g => g.people.forEach(pid => s.add(pid)));
    return s;
  }, [cur]);

  const unassigned = useMemo(
    () => people.filter(p => !assignedSet.has(p.id)),
    [people, assignedSet]
  );

  const filteredUnassigned = useMemo(() => {
    if (!search) return unassigned;
    const t = search.toLowerCase();
    return unassigned.filter(p =>
      Object.values(p).some(v => String(v).toLowerCase().includes(t))
    );
  }, [unassigned, search]);

  const ruleEvals = useMemo(() => evalRules(rules, activities), [rules, activities]);
  const brokenRulesAll = useMemo(() => ruleEvals.filter(e => e.broken), [ruleEvals]);
  const brokenRulesHere = useMemo(() => {
    if (!cur) return [];
    return brokenRulesAll.filter(({ rule }) => {
      if (rule.activity_types && rule.activity_types.length) {
        if (!rule.activity_types.includes(cur.type)) return false;
      }
      const gA = cur.groups.find(g => g.people.includes(rule.person_a));
      const gB = cur.groups.find(g => g.people.includes(rule.person_b));
      return !!(gA && gB);
    });
  }, [brokenRulesAll, cur]);

  const connections = useMemo(() => {
    if (!selP) return [];
    const c = {};
    activities.forEach(a => a.groups.forEach(gr => {
      if (gr.people.includes(selP)) {
        gr.people.forEach(pid => {
          if (pid !== selP) {
            if (!c[pid]) c[pid] = { count: 0, contexts: [] };
            c[pid].count++;
            c[pid].contexts.push({ activity: a.name, group: gr.name, type: a.type });
          }
        });
      }
    }));
    return Object.entries(c)
      .map(([pid, d]) => ({ person: people.find(p => p.id === pid), ...d }))
      .filter(x => x.person)
      .sort((a, b) => b.count - a.count);
  }, [selP, activities, people]);

  const selData = people.find(p => p.id === selP);

  // ----- mutation helpers -----

  // Apply a mutation to the current activity's groups locally, then sync
  // changed memberships / hosts to Supabase.
  const mutateCurrent = (transform) => {
    if (!cur) return;
    const nextGroups = transform(cur.groups);
    setActivities(prev => prev.map(a => a.id === activeA ? { ...a, groups: nextGroups } : a));
    nextGroups.forEach((gr, idx) => {
      const orig = cur.groups.find(g => g.id === gr.id) || cur.groups[idx];
      if (!orig) return;
      const membersChanged = orig.people.join(",") !== gr.people.join(",");
      const hostChanged = (orig.host ?? null) !== (gr.host ?? null);
      if (membersChanged) setGroupMembers(gr.id, gr.people).catch(console.error);
      if (hostChanged) updateGroup(gr.id, { host: gr.host }).catch(console.error);
    });
  };

  const handleSaveSeating = async (activityId, groupId, seatingConfig) => {
    setActivities(prev => prev.map(a => a.id !== activityId ? a : ({
      ...a,
      groups: a.groups.map(g => g.id === groupId ? { ...g, seatingConfig } : g),
    })));
    try { await updateGroup(groupId, { seatingConfig }); }
    catch (e) { console.error(e); }
  };

  const handleCSV = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = async (ev) => {
      const lines = ev.target.result.trim().split(/\r?\n/);
      if (lines.length < 2) return;
      const heads = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
      const nk = heads.find(h => /name/i.test(h)) || heads[0];
      const parsed = lines.slice(1).map(line => {
        const v = []; let c = ""; let q = false;
        for (let i = 0; i < line.length; i++) {
          if (line[i] === '"') q = !q;
          else if (line[i] === "," && !q) { v.push(c.trim()); c = ""; }
          else c += line[i];
        }
        v.push(c.trim());
        const o = {};
        heads.forEach((h, i) => { o[h === nk ? "Name" : h] = v[i] || ""; });
        return o;
      }).filter(r => r.Name);
      const ex = new Set(people.map(p => p.Name?.toLowerCase()));
      const fresh = parsed.filter(p => !ex.has(p.Name.toLowerCase()));
      if (!fresh.length) return;
      try {
        const inserted = await addPeople(eventId, fresh, people.length);
        setPeople(prev => [...prev, ...inserted]);
      } catch (err) {
        console.error(err);
        alert("CSV import failed: " + err.message);
      }
    };
    r.readAsText(f);
    e.target.value = "";
  };

  const addPersonNew = async () => {
    const name = newPerson.trim();
    if (!name) return;
    if (people.some(p => p.Name?.toLowerCase() === name.toLowerCase())) {
      alert(`"${name}" is already in this event.`);
      return;
    }
    try {
      const inserted = await addPeople(eventId, [{ Name: name }], people.length);
      setPeople(prev => [...prev, ...inserted]);
      setNewPerson("");
      setAddingPerson(false);
    } catch (e) {
      console.error(e);
      alert("Add person failed: " + e.message);
    }
  };

  const removePerson = async (pid) => {
    const p = people.find(x => x.id === pid);
    if (!p) return;
    if (!confirm(`Remove "${p.Name}" from this event? They'll be removed from every activity and group.`)) return;
    try {
      await deletePerson(pid);
      setPeople(prev => prev.filter(x => x.id !== pid));
      setActivities(prev => prev.map(a => ({
        ...a,
        groups: a.groups.map(gr => ({
          ...gr,
          people: gr.people.filter(id => id !== pid),
          host: gr.host === pid ? null : gr.host,
        })),
      })));
      if (selP === pid) { setSelP(null); setSideOpen(false); }
    } catch (e) {
      console.error(e);
      alert("Remove failed: " + e.message);
    }
  };

  const handleAddRule = async (payload) => {
    const r = await createRule(eventId, payload);
    setRules(prev => [...prev, r]);
  };

  const handleDeleteRule = async (id) => {
    await deleteRule(id);
    setRules(prev => prev.filter(r => r.id !== id));
  };

  const openRebalance = () => {
    const proposals = proposeSwaps(rules, activities);
    setRebalanceProposals(proposals);
  };

  const applyProposal = async (swap) => {
    setProposalBusy(true);
    try {
      let affected = null;
      setActivities(prev => prev.map(act => {
        if (act.id !== swap.activityId) return act;
        const newGroups = act.groups.map(gr => {
          if (gr.id === swap.aGroupId) {
            return { ...gr, people: gr.people.map(p => p === swap.aPersonId ? swap.bPersonId : p), host: gr.host === swap.aPersonId ? swap.bPersonId : gr.host };
          }
          if (gr.id === swap.bGroupId) {
            return { ...gr, people: gr.people.map(p => p === swap.bPersonId ? swap.aPersonId : p), host: gr.host === swap.bPersonId ? swap.aPersonId : gr.host };
          }
          return gr;
        });
        affected = newGroups.filter(g => g.id === swap.aGroupId || g.id === swap.bGroupId);
        return { ...act, groups: newGroups };
      }));
      if (affected) {
        await Promise.all(affected.map(g => setGroupMembers(g.id, g.people)));
        await Promise.all(affected.map(g => updateGroup(g.id, { host: g.host })));
      }
    } catch (e) {
      console.error(e);
      alert("Apply failed: " + e.message);
    } finally {
      setProposalBusy(false);
    }
  };

  const acceptProposal = async (idx) => {
    const p = rebalanceProposals[idx];
    if (!p) return;
    await applyProposal(p.swap);
    setRebalanceProposals(prev => prev.filter((_, i) => i !== idx));
  };
  const rejectProposal = (idx) =>
    setRebalanceProposals(prev => prev.filter((_, i) => i !== idx));

  const addActivityNew = async () => {
    const name = naName.trim();
    if (!name) return;
    const defaultGroupSize = parseInt(naMaxSize) || null;
    try {
      const id = await dbAddActivity(eventId, {
        name, type: naType, position: activities.length, defaultGroupSize,
      });
      setActivities(prev => [...prev, { id, name, type: naType, defaultGroupSize, groups: [] }]);
      setActiveA(id);
      setShowNewA(false);
      setNaName("");
      setNaMaxSize("");
    } catch (e) {
      console.error(e);
      alert("Add activity failed: " + e.message);
    }
  };

  const moveActivity = async (id, dir) => {
    const idx = activities.findIndex(a => a.id === id);
    const next = idx + dir;
    if (idx < 0 || next < 0 || next >= activities.length) return;
    const swapped = [...activities];
    [swapped[idx], swapped[next]] = [swapped[next], swapped[idx]];
    setActivities(swapped);
    try {
      await Promise.all([
        updateActivity(swapped[idx].id, { position: idx }),
        updateActivity(swapped[next].id, { position: next }),
      ]);
    } catch (e) {
      console.error(e);
      alert("Reorder failed: " + e.message);
    }
  };

  const delActivity = async (id) => {
    try {
      await dbDeleteActivity(id);
      setActivities(prev => prev.filter(a => a.id !== id));
      if (activeA === id) setActiveA(activities.find(a => a.id !== id)?.id || null);
    } catch (e) {
      console.error(e);
      alert("Delete failed: " + e.message);
    }
  };

  const addGroupNew = async () => {
    if (!cur || !ngrName.trim()) return;
    const capacity = parseInt(ngrCap) || null;
    try {
      const id = await dbAddGroup(cur.id, { name: ngrName.trim(), capacity, position: cur.groups.length });
      setActivities(prev => prev.map(a => a.id === activeA
        ? { ...a, groups: [...a.groups, { id, name: ngrName.trim(), capacity, host: null, people: [] }] }
        : a
      ));
      setShowNewGr(false);
      setNgrName("");
      setNgrCap("");
    } catch (e) {
      console.error(e);
      alert("Add group failed: " + e.message);
    }
  };

  const delGroup = async (gid) => {
    const group = cur?.groups.find(g => g.id === gid);
    if (!group) return;
    if (!confirm(`Delete "${group.name}"?`)) return;
    try {
      await dbDeleteGroup(gid);
      setActivities(prev => prev.map(a => a.id === activeA
        ? { ...a, groups: a.groups.filter(g => g.id !== gid) }
        : a
      ));
    } catch (e) {
      console.error(e);
      alert("Delete group failed: " + e.message);
    }
  };

  const toggleHost = (pid, gid) =>
    mutateCurrent(groups => groups.map(gr => gr.id === gid
      ? { ...gr, host: gr.host === pid ? null : pid }
      : gr
    ));

  const onDragStart = (e, pid) => {
    setDragPid(pid);
    e.dataTransfer.effectAllowed = "move";
  };

  const dropToGroup = (e, gid) => {
    e.preventDefault();
    if (!dragPid) return;
    mutateCurrent(groups => groups.map(gr => {
      const had = gr.people.includes(dragPid);
      let pp = gr.people.filter(id => id !== dragPid);
      if (gr.id === gid && !had) pp = [...pp, dragPid];
      let h = gr.host;
      if (had && gr.id !== gid && gr.host === dragPid) h = null;
      return { ...gr, people: pp, host: h };
    }));
    setDragPid(null);
  };

  const dropToPool = (e) => {
    e.preventDefault();
    if (!dragPid) return;
    mutateCurrent(groups => groups.map(gr => {
      const had = gr.people.includes(dragPid);
      return {
        ...gr,
        people: gr.people.filter(id => id !== dragPid),
        host: had && gr.host === dragPid ? null : gr.host,
      };
    }));
    setDragPid(null);
  };

  const removeFrom = (pid, gid) =>
    mutateCurrent(groups => groups.map(gr => gr.id === gid
      ? { ...gr, people: gr.people.filter(id => id !== pid), host: gr.host === pid ? null : gr.host }
      : gr
    ));

  const moveInGroup = (pid, gid, dir) =>
    mutateCurrent(groups => groups.map(gr => {
      if (gr.id !== gid) return gr;
      const idx = gr.people.indexOf(pid);
      const next = idx + dir;
      if (idx < 0 || next < 0 || next >= gr.people.length) return gr;
      const pp = [...gr.people];
      [pp[idx], pp[next]] = [pp[next], pp[idx]];
      return { ...gr, people: pp };
    }));

  const doSwap = (tPid, tGid) => {
    if (!swapSrc) return;
    mutateCurrent(groups => groups.map(gr => {
      let pp = [...gr.people];
      if (gr.id === swapSrc.groupId) pp = pp.map(id => id === swapSrc.personId ? tPid : id);
      if (gr.id === tGid) pp = pp.map(id => id === tPid ? swapSrc.personId : id);
      let h = gr.host;
      if (gr.id === swapSrc.groupId && h === swapSrc.personId) h = tPid;
      if (gr.id === tGid && h === tPid) h = swapSrc.personId;
      return { ...gr, people: pp, host: h };
    }));
    setSwapSrc(null);
  };

  const selectP = (pid) => { setSelP(pid); setSideOpen(true); };

  const autoFill = async () => {
    if (!cur) return;
    const unassignedIds = unassigned.map(p => p.id);
    if (unassignedIds.length === 0) return;
    const defSize = cur.defaultGroupSize ?? null;

    const createMoreGroups = async (count, startPos) => {
      const label = ti.groupLabel || "Group";
      const created = [];
      for (let i = 0; i < count; i++) {
        const name = `${label} ${startPos + i + 1}`;
        const id = await dbAddGroup(cur.id, { name, capacity: defSize, position: startPos + i });
        created.push({ id, name, capacity: defSize, host: null, people: [] });
      }
      return created;
    };

    let finalGroups = [...cur.groups];
    const totalAssigned = finalGroups.reduce((a, g) => a + g.people.length, 0);
    const needed = unassignedIds.length + totalAssigned;

    try {
      if (finalGroups.length === 0) {
        if (!defSize) {
          alert("Add a group first, or set a max group size on the activity so Auto-fill can create groups for you.");
          return;
        }
        const num = Math.ceil(needed / defSize);
        if (!confirm(`No groups yet. Create ${num} group${num === 1 ? "" : "s"} of size ${defSize} to fit ${needed} ${needed === 1 ? "person" : "people"}?`)) return;
        finalGroups = await createMoreGroups(num, 0);
      } else if (defSize) {
        const totalCap = finalGroups.reduce((a, g) => a + (g.capacity ?? defSize), 0);
        if (needed > totalCap) {
          const extra = Math.ceil((needed - totalCap) / defSize);
          if (confirm(`You have ${needed} people to seat but only ${totalCap} seats across ${finalGroups.length} group${finalGroups.length === 1 ? "" : "s"}. Create ${extra} more group${extra === 1 ? "" : "s"} of size ${defSize}?`)) {
            const extras = await createMoreGroups(extra, finalGroups.length);
            finalGroups = [...finalGroups, ...extras];
          }
        }
      }
    } catch (e) {
      console.error(e);
      alert("Creating groups failed: " + e.message);
      return;
    }

    // Rule-aware allocation: shuffle people (Fisher-Yates), then place each
    // into the group that introduces the fewest new rule violations (ties
    // broken by smallest current group size, then random).
    const shuffled = [...unassignedIds];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    let working = activities.map(a => a.id === activeA
      ? { ...a, groups: finalGroups.map(g => ({ ...g, people: [...g.people] })) }
      : a
    );
    for (const personId of shuffled) {
      const currAct = working.find(a => a.id === activeA);
      const underCap = currAct.groups.filter(g => {
        const eff = g.capacity ?? defSize;
        return eff == null || g.people.length < eff;
      });
      const candidates = (underCap.length ? underCap : currAct.groups).map(g => g.id);
      const chosen = pickGroupForPlacement(rules, working, activeA, personId, candidates);
      working = applyPlacement(working, personId, activeA, chosen);
    }

    setActivities(working);
    const finalAct = working.find(a => a.id === activeA);
    const origById = new Map(cur.groups.map(g => [g.id, g]));
    for (const gr of finalAct.groups) {
      const orig = origById.get(gr.id);
      if (!orig || orig.people.join(",") !== gr.people.join(",")) {
        setGroupMembers(gr.id, gr.people).catch(console.error);
      }
    }
  };

  const clearAll = () => {
    if (!cur) return;
    if (!confirm("Remove everyone from all groups in this activity?")) return;
    mutateCurrent(groups => groups.map(gr => ({ ...gr, people: [], host: null })));
  };

  // ----- render -----

  if (loading) {
    return (
      <div style={{ height: "100vh", background: "var(--paper-0)", color: "var(--ink)", fontFamily: "var(--ui)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontFamily: "var(--display)", fontSize: 22, fontStyle: "italic", marginBottom: 6 }}>Placement</div>
          <div style={{ color: "var(--ink-3)", fontSize: 13 }}>Loading {eventName}…</div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ height: "100vh", background: "var(--paper-0)", color: "var(--ink)", fontFamily: "var(--ui)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ maxWidth: 520 }}>
          <Eyebrow style={{ marginBottom: 6 }}>Trouble</Eyebrow>
          <Display size={22} style={{ marginBottom: 10 }}>Couldn't load this event</Display>
          <div style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 16 }}>{loadError}</div>
          <Btn variant="ghost" size="sm" icon={I.arrowLeft} onClick={onBack}>Back to events</Btn>
        </div>
      </div>
    );
  }

  if (exportMode) {
    return (
      <ExportView
        activities={activities}
        people={people}
        eventName={eventName}
        filterActivityId={exportMode === "all" ? null : exportMode}
        onClose={() => setExportMode(null)}
      />
    );
  }

  const moments = activities.length;
  const daysCount = new Set(activities.map(a => a.day).filter(Boolean)).size || 0;

  return (
    <div style={{
      height: "100vh",
      background: "var(--paper-0)",
      color: "var(--ink)",
      fontFamily: "var(--ui)",
      display: "flex", flexDirection: "column",
      overflow: "hidden",
      position: "relative",
    }}>
      {/* Top bar */}
      <div style={{
        padding: "14px 28px",
        borderBottom: "1px solid var(--line)",
        display: "flex", alignItems: "center", gap: 14,
        background: "var(--paper-0)",
        flexShrink: 0,
      }}>
        <img src="/atrum-logo-white.png" className="brand-logo" alt="Atrum" style={{ width: 22, height: 24, objectFit: "contain" }} />
        <div style={{ width: 1, height: 22, background: "var(--line-2)" }} />
        <button
          type="button"
          onClick={onBack}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "none", border: "none", color: "var(--ink-3)",
            cursor: "pointer", fontFamily: "var(--ui)", fontSize: 12.5,
            padding: 0,
          }}
        >
          <I.arrowLeft size={13} stroke="currentColor" /> All events
        </button>
        <div style={{ width: 1, height: 18, background: "var(--line-2)" }} />
        <div>
          <div style={{ fontFamily: "var(--display)", fontSize: 18, fontWeight: 400, letterSpacing: -0.1 }}>
            {eventName}
          </div>
          <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 1, letterSpacing: 0.3 }}>
            {people.length} guest{people.length === 1 ? "" : "s"}
            {daysCount > 0 && ` · ${daysCount} day${daysCount === 1 ? "" : "s"}`}
            {` · ${moments} moment${moments === 1 ? "" : "s"} planned`}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <input ref={csvRef} type="file" accept=".csv" onChange={handleCSV} style={{ display: "none" }} />
        <Btn variant="quiet" size="sm" icon={I.upload} onClick={() => csvRef.current?.click()}>Import CSV</Btn>
        <Btn variant="quiet" size="sm" icon={I.tune} onClick={() => setShowRules(true)}>
          Rules · {rules.length}
          {brokenRulesAll.length > 0 && (
            <span style={{
              marginLeft: 6, padding: "1px 6px", borderRadius: 4,
              background: "var(--warn-soft)", color: "var(--warn)",
              fontSize: 10.5, fontWeight: 600,
            }}>{brokenRulesAll.length} broken</span>
          )}
        </Btn>
        <Btn variant="ghost" size="sm" icon={I.printer} onClick={() => setExportMode("all")}>Export PDF</Btn>
        <button
          ref={settingsBtnRef}
          type="button"
          onClick={() => setSettingsOpen(v => !v)}
          title="Appearance"
          style={{
            width: 32, height: 32, borderRadius: 8,
            border: "1px solid var(--line-2)",
            background: "transparent",
            color: "var(--ink-3)",
            cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <I.settings size={14} stroke="currentColor" />
        </button>
        {session && <UserMenu session={session} size={32} />}
      </div>

      {settingsOpen && (
        <SettingsPopover
          anchor={settingsBtnRef.current}
          theme={theme}
          setTheme={setTheme}
          navMode={navMode}
          setNavMode={setNavMode}
          poolPosition={poolPosition}
          setPoolPosition={setPoolPosition}
          onClose={() => setSettingsOpen(false)}
        />
      )}

      {/* Topbar mode: activity tabs */}
      {navMode === "topbar" && activities.length > 0 && (
        <div style={{
          padding: "0 28px",
          borderBottom: "1px solid var(--line)",
          display: "flex", alignItems: "stretch", gap: 0,
          background: "var(--paper-0)", overflowX: "auto",
          flexShrink: 0,
        }}>
          {activities.map(a => {
            const t = typeOf(a.type);
            const Icon = t.Icon;
            const active = a.id === activeA;
            const seated = a.groups.reduce((x, g) => x + g.people.length, 0);
            return (
              <div
                key={a.id}
                onClick={() => setActiveA(a.id)}
                style={{
                  padding: "14px 18px",
                  cursor: "pointer",
                  borderBottom: `2px solid ${active ? "var(--ink)" : "transparent"}`,
                  display: "flex", alignItems: "center", gap: 10,
                  color: active ? "var(--ink)" : "var(--ink-3)",
                  marginBottom: -1,
                  whiteSpace: "nowrap",
                }}
              >
                <Icon size={15} stroke="currentColor" />
                <div>
                  <div style={{ fontSize: 13, fontWeight: active ? 600 : 500, letterSpacing: -0.05 }}>{a.name}</div>
                  <div style={{ fontSize: 10.5, color: "var(--ink-3)", marginTop: 1 }}>
                    {seated}/{people.length} seated · {a.groups.length} {t.groupLabel.toLowerCase()}{a.groups.length === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
            );
          })}
          <div style={{ display: "flex", alignItems: "center", padding: "0 12px" }}>
            <Btn variant="quiet" size="sm" icon={I.plus} onClick={() => setShowNewA(true)}>Activity</Btn>
          </div>
        </div>
      )}

      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Sidebar mode */}
        {navMode === "sidebar" && (
          <div style={{
            width: 270, flexShrink: 0,
            borderRight: "1px solid var(--line)",
            background: "var(--paper-1)",
            display: "flex", flexDirection: "column",
          }}>
            <div style={{ padding: "18px 18px 10px" }}>
              <Eyebrow>Activities</Eyebrow>
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: "0 10px 10px" }}>
              {activities.map((a, idx) => {
                const t = typeOf(a.type);
                const Icon = t.Icon;
                const active = a.id === activeA;
                const seated = a.groups.reduce((x, g) => x + g.people.length, 0);
                const total = people.length || 1;
                const canUp = idx > 0;
                const canDown = idx < activities.length - 1;
                return (
                  <div
                    key={a.id}
                    onClick={() => setActiveA(a.id)}
                    style={{
                      padding: "11px 12px",
                      borderRadius: 8,
                      cursor: "pointer",
                      background: active ? "var(--paper-0)" : "transparent",
                      border: `1px solid ${active ? "var(--line-2)" : "transparent"}`,
                      boxShadow: active ? "0 1px 2px rgba(34,28,20,0.04)" : "none",
                      marginBottom: 2,
                      display: "flex", alignItems: "flex-start", gap: 10,
                      transition: "background 0.12s, border-color 0.12s",
                    }}
                  >
                    <div style={{
                      width: 30, height: 30, flexShrink: 0,
                      borderRadius: 6,
                      background: active ? "var(--ink)" : "var(--paper-2)",
                      color: active ? "var(--paper-0)" : "var(--ink-2)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      border: `1px solid ${active ? "var(--ink)" : "var(--line)"}`,
                    }}>
                      <Icon size={15} stroke="currentColor" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 600, color: "var(--ink)",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        letterSpacing: -0.05,
                      }}>{a.name}</div>
                      <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
                        {t.label}
                        {a.defaultGroupSize ? ` · ${t.groupLabel.toLowerCase()}s of ${a.defaultGroupSize}` : ""}
                      </div>
                      <div style={{
                        marginTop: 6, height: 3, borderRadius: 2,
                        background: "var(--line)", overflow: "hidden",
                      }}>
                        <div style={{
                          width: `${Math.min(100, (seated / total) * 100)}%`,
                          height: "100%",
                          background: active ? "var(--accent)" : "var(--ink-3)",
                        }} />
                      </div>
                      <div style={{ fontSize: 10.5, color: "var(--ink-3)", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
                        {seated} of {people.length} placed · {a.groups.length} {t.groupLabel.toLowerCase()}{a.groups.length === 1 ? "" : "s"}
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                      <IconBtn icon={I.chevronUp} size={10} title="Move up" disabled={!canUp} onClick={() => moveActivity(a.id, -1)} />
                      <IconBtn icon={I.chevronDown} size={10} title="Move down" disabled={!canDown} onClick={() => moveActivity(a.id, 1)} />
                      <IconBtn icon={I.trash} size={10} title="Delete activity" onClick={() => {
                        if (confirm(`Delete "${a.name}"?`)) delActivity(a.id);
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ padding: "8px 12px 14px", borderTop: "1px solid var(--line)" }}>
              {!showNewA ? (
                <Btn variant="dashed" size="sm" icon={I.plus} style={{ width: "100%" }} onClick={() => setShowNewA(true)}>
                  New activity
                </Btn>
              ) : (
                <NewActivityForm
                  naName={naName} setNaName={setNaName}
                  naType={naType} setNaType={setNaType}
                  naMaxSize={naMaxSize} setNaMaxSize={setNaMaxSize}
                  onAdd={addActivityNew}
                  onCancel={() => { setShowNewA(false); setNaName(""); setNaMaxSize(""); }}
                />
              )}
            </div>
          </div>
        )}

        {/* Main */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          {!cur ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ink-3)" }}>
              <div style={{ textAlign: "center", maxWidth: 360 }}>
                <Eyebrow style={{ marginBottom: 8 }}>No moments yet</Eyebrow>
                <Display size={22} style={{ marginBottom: 10 }}>Create your first activity</Display>
                <div style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 18 }}>
                  Activities are the dinners, transfers, salons and excursions that shape a gathering.
                </div>
                <Btn variant="primary" size="sm" icon={I.plus} onClick={() => setShowNewA(true)}>New activity</Btn>
              </div>
            </div>
          ) : (
            <EditorMain
              cur={cur}
              ti={ti}
              people={people}
              unassigned={unassigned}
              filteredUnassigned={filteredUnassigned}
              assignedSet={assignedSet}
              brokenRulesHere={brokenRulesHere}
              brokenRulesAll={brokenRulesAll}
              poolPosition={poolPosition}
              showHosts={showHosts} setShowHosts={setShowHosts}
              search={search} setSearch={setSearch}
              addingPerson={addingPerson} setAddingPerson={setAddingPerson}
              newPerson={newPerson} setNewPerson={setNewPerson}
              onAddPerson={addPersonNew}
              onDragStart={onDragStart}
              onDropToPool={dropToPool}
              onDropToGroup={dropToGroup}
              selectedP={selP}
              onPersonClick={selectP}
              onToggleHost={toggleHost}
              onMoveInGroup={moveInGroup}
              onRemoveFrom={removeFrom}
              onSwap={(pid, gid) => setSwapSrc({ personId: pid, groupId: gid })}
              onDeleteGroup={delGroup}
              onAutoFill={autoFill}
              onClear={clearAll}
              onOpenRebalance={openRebalance}
              onExportActivity={() => setExportMode(cur.id)}
              onOpenSeating={(groupId) => setSeatingFor({ activityId: cur.id, groupId })}
              showNewGr={showNewGr} setShowNewGr={setShowNewGr}
              ngrName={ngrName} setNgrName={setNgrName}
              ngrCap={ngrCap} setNgrCap={setNgrCap}
              onAddGroup={addGroupNew}
            />
          )}
        </div>

        {/* Right connections panel */}
        {sideOpen && selData && (
          <ConnectionsPanel
            selData={selData}
            ti={ti}
            connections={connections}
            activities={activities}
            selP={selP}
            onClose={() => { setSideOpen(false); setSelP(null); }}
            onRemove={() => removePerson(selP)}
            onJump={(pid) => { setSelP(pid); }}
          />
        )}
      </div>

      {/* Overlays */}
      {swapSrc && cur && (
        <SwapOverlay
          swapSrc={swapSrc}
          groups={cur.groups}
          people={people}
          onSwap={doSwap}
          onCancel={() => setSwapSrc(null)}
        />
      )}
      {showRules && (
        <RulesOverlay
          rules={rules}
          people={people}
          activities={activities}
          gtypes={GTYPES}
          onAdd={handleAddRule}
          onDelete={handleDeleteRule}
          onClose={() => setShowRules(false)}
        />
      )}
      {rebalanceProposals !== null && (
        <RebalanceOverlay
          proposals={rebalanceProposals}
          rules={rules}
          people={people}
          activities={activities}
          busy={proposalBusy}
          onAccept={acceptProposal}
          onReject={rejectProposal}
          onClose={() => setRebalanceProposals(null)}
        />
      )}
      {seatingFor && (() => {
        const a = activities.find(x => x.id === seatingFor.activityId);
        const g = a?.groups.find(x => x.id === seatingFor.groupId);
        if (!a || !g) return null;
        return (
          <SeatingModal
            group={g}
            activity={a}
            people={people}
            onSave={(cfg) => handleSaveSeating(a.id, g.id, cfg)}
            onClose={() => setSeatingFor(null)}
          />
        );
      })()}
    </div>
  );
}

function NewActivityForm({ naName, setNaName, naType, setNaType, naMaxSize, setNaMaxSize, onAdd, onCancel }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <input
        autoFocus
        value={naName}
        onChange={(e) => setNaName(e.target.value)}
        placeholder="Activity name"
        onKeyDown={(e) => { if (e.key === "Enter") onAdd(); if (e.key === "Escape") onCancel(); }}
        style={inputStyle}
      />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {GTYPES.map(t => {
          const on = naType === t.value;
          const Icon = t.Icon;
          return (
            <button
              key={t.value}
              type="button"
              title={t.label}
              onClick={() => setNaType(t.value)}
              style={{
                padding: "4px 8px", borderRadius: 6,
                border: `1px solid ${on ? "var(--ink)" : "var(--line-2)"}`,
                background: on ? "var(--ink)" : "transparent",
                color: on ? "var(--paper-0)" : "var(--ink-3)",
                fontSize: 11, cursor: "pointer", fontFamily: "var(--ui)",
                display: "inline-flex", alignItems: "center", gap: 4,
                transition: "all 0.12s",
              }}
            >
              <Icon size={11} stroke="currentColor" />
              {t.label}
            </button>
          );
        })}
      </div>
      <input
        value={naMaxSize}
        onChange={(e) => setNaMaxSize(e.target.value)}
        placeholder="Max group size (optional)"
        type="number"
        min="1"
        style={inputStyle}
      />
      <div style={{ display: "flex", gap: 5 }}>
        <Btn variant="primary" size="sm" style={{ flex: 1 }} onClick={onAdd}>Add</Btn>
        <Btn variant="quiet" size="sm" onClick={onCancel}>Cancel</Btn>
      </div>
    </div>
  );
}

function EditorMain({
  cur, ti, people, unassigned, filteredUnassigned, assignedSet,
  brokenRulesHere,
  poolPosition,
  showHosts, setShowHosts,
  search, setSearch,
  addingPerson, setAddingPerson,
  newPerson, setNewPerson,
  onAddPerson,
  onDragStart, onDropToPool, onDropToGroup,
  selectedP, onPersonClick,
  onToggleHost, onMoveInGroup, onRemoveFrom, onSwap, onDeleteGroup,
  onAutoFill, onClear, onOpenRebalance, onExportActivity,
  onOpenSeating,
  showNewGr, setShowNewGr,
  ngrName, setNgrName, ngrCap, setNgrCap, onAddGroup,
}) {
  const totalAssigned = assignedSet.size;
  const label = ti.groupLabel.toLowerCase();

  return (
    <>
      {/* Activity header */}
      <div style={{
        padding: "24px 32px 18px",
        borderBottom: "1px solid var(--line)",
        display: "flex", alignItems: "flex-end", gap: 20,
        flexWrap: "wrap",
        flexShrink: 0,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Eyebrow style={{ marginBottom: 8 }}>{ti.label}</Eyebrow>
          <Display size={30} style={{ marginBottom: 6 }}>{cur.name}</Display>
          <div style={{ fontSize: 13, color: "var(--ink-2)", fontVariantNumeric: "tabular-nums" }}>
            <strong style={{ fontWeight: 600, color: "var(--ink)" }}>{totalAssigned}</strong> of {people.length} guests placed
            {" · "}<strong style={{ fontWeight: 600, color: "var(--ink)" }}>{unassigned.length}</strong> still to seat
            {" · "}{cur.groups.length} {label}{cur.groups.length === 1 ? "" : "s"}
            {cur.defaultGroupSize ? <> · {label}s of {cur.defaultGroupSize}</> : null}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Toggle value={showHosts} onChange={setShowHosts} label="Hosts" icon={I.star} />
          <Btn variant="ghost" size="sm" icon={I.sparkle} onClick={onAutoFill}>Auto-fill</Btn>
          <Btn variant="quiet" size="sm" icon={I.refresh} onClick={onClear}>Clear</Btn>
          <Btn variant="quiet" size="sm" icon={I.printer} onClick={onExportActivity}>Export activity</Btn>
        </div>
      </div>

      {/* Broken rules banner */}
      {brokenRulesHere.length > 0 && (
        <div style={{
          padding: "12px 32px",
          background: "var(--warn-soft)",
          borderBottom: "1px solid var(--warn-line)",
          display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
          flexShrink: 0,
        }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "var(--warn)" }}>
            <I.alert size={14} stroke="currentColor" />
            <span style={{ fontSize: 12, fontWeight: 600 }}>
              {brokenRulesHere.length} rule{brokenRulesHere.length === 1 ? "" : "s"} to reconsider
            </span>
          </div>
          {brokenRulesHere.slice(0, 3).map(({ rule, reason }) => {
            const a = people.find(p => p.id === rule.person_a);
            const b = people.find(p => p.id === rule.person_b);
            const together = rule.type === "always_with" || rule.type === "at_least";
            return (
              <div key={rule.id} style={{ fontSize: 12, color: "var(--warn-ink)" }}>
                <strong>{a?.Name?.split(" ")[0]}</strong> &amp; <strong>{b?.Name?.split(" ")[0]}</strong>
                {" "}should sit {together ? "together" : "apart"}
                <span style={{ color: "var(--warn-ink)", opacity: 0.7 }}> — {reason}</span>
              </div>
            );
          })}
          <div style={{ flex: 1 }} />
          <Btn variant="ghost" size="sm" icon={I.sparkle} onClick={onOpenRebalance}>Propose swaps</Btn>
        </div>
      )}

      <div style={{
        flex: 1, overflow: "auto",
        display: "flex",
        flexDirection: poolPosition === "side" ? "row" : "column",
        minHeight: 0,
      }}>
        {poolPosition === "top" ? (
          <GuestPoolTop
            filteredUnassigned={filteredUnassigned}
            unassigned={unassigned}
            search={search} setSearch={setSearch}
            addingPerson={addingPerson} setAddingPerson={setAddingPerson}
            newPerson={newPerson} setNewPerson={setNewPerson}
            onAddPerson={onAddPerson}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDropToPool}
            selectedP={selectedP}
            onPersonClick={onPersonClick}
            onPersonDragStart={onDragStart}
          />
        ) : (
          <GuestPoolSide
            filteredUnassigned={filteredUnassigned}
            unassigned={unassigned}
            search={search} setSearch={setSearch}
            addingPerson={addingPerson} setAddingPerson={setAddingPerson}
            newPerson={newPerson} setNewPerson={setNewPerson}
            onAddPerson={onAddPerson}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDropToPool}
            selectedP={selectedP}
            onPersonClick={onPersonClick}
            onPersonDragStart={onDragStart}
          />
        )}

        <div style={{ flex: 1, padding: "22px 32px 40px", minWidth: 0 }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 14, alignItems: "start",
          }}>
            {cur.groups.map((group, gi) => (
              <GroupCard
                key={group.id}
                group={group}
                gi={gi}
                defaultSize={cur.defaultGroupSize}
                people={people}
                showHosts={showHosts}
                selectedP={selectedP}
                activityType={cur.type}
                onOpenSeating={() => onOpenSeating(group.id)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDropToGroup(e, group.id)}
                onPersonClick={onPersonClick}
                onPersonDragStart={onDragStart}
                onToggleHost={(pid) => onToggleHost(pid, group.id)}
                onMoveUp={(pid) => onMoveInGroup(pid, group.id, -1)}
                onMoveDown={(pid) => onMoveInGroup(pid, group.id, 1)}
                onRemove={(pid) => onRemoveFrom(pid, group.id)}
                onSwap={(pid) => onSwap(pid, group.id)}
                onDeleteGroup={() => onDeleteGroup(group.id)}
              />
            ))}

            {!showNewGr ? (
              <div
                onClick={() => {
                  setShowNewGr(true);
                  setNgrName(`${ti.groupLabel} ${cur.groups.length + 1}`);
                  setNgrCap(cur.defaultGroupSize ? String(cur.defaultGroupSize) : "");
                }}
                style={{
                  border: "1px dashed var(--line-2)",
                  borderRadius: 12,
                  padding: "32px 20px",
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  cursor: "pointer",
                  color: "var(--ink-3)",
                  fontSize: 13,
                  gap: 6,
                  minHeight: 160,
                  transition: "background 0.12s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "var(--paper-1)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <I.plus size={18} stroke="currentColor" />
                <div>Add {label}</div>
              </div>
            ) : (
              <div style={{
                border: "1px solid var(--line-2)",
                borderRadius: 12,
                padding: 16,
                background: "var(--paper-1)",
              }}>
                <Eyebrow style={{ marginBottom: 10 }}>New {label}</Eyebrow>
                <input
                  autoFocus
                  value={ngrName}
                  onChange={(e) => setNgrName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") onAddGroup(); if (e.key === "Escape") setShowNewGr(false); }}
                  style={inputStyle}
                  placeholder="Name"
                />
                <input
                  value={ngrCap}
                  onChange={(e) => setNgrCap(e.target.value)}
                  type="number"
                  min="1"
                  style={inputStyle}
                  placeholder="Capacity (optional)"
                />
                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                  <Btn variant="primary" size="sm" onClick={onAddGroup} style={{ flex: 1 }}>Add</Btn>
                  <Btn variant="quiet" size="sm" onClick={() => setShowNewGr(false)}>Cancel</Btn>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function ConnectionsPanel({ selData, ti, connections, activities, onClose, onRemove, onJump }) {
  const initials = (selData.Name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const label = (ti?.groupLabel || "Group").toLowerCase();
  const firstName = (selData.Name || "").split(" ")[0];
  const maxBars = 6;

  return (
    <div style={{
      width: 320, flexShrink: 0,
      borderLeft: "1px solid var(--line)",
      background: "var(--paper-1)",
      display: "flex", flexDirection: "column",
      overflow: "auto",
    }}>
      <div style={{
        padding: "18px 20px",
        borderBottom: "1px solid var(--line)",
        display: "flex", alignItems: "flex-start", gap: 10,
      }}>
        <div style={{
          width: 38, height: 38, borderRadius: "50%",
          background: "var(--paper-2)", border: "1px solid var(--line)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 12, fontWeight: 600, color: "var(--ink-2)", flexShrink: 0,
        }}>{initials}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "var(--display)", fontSize: 18, letterSpacing: -0.1 }}>
            {selData.Name}
          </div>
          {(selData.Role || selData.Company) && (
            <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
              {[selData.Role, selData.Company].filter(Boolean).join(" · ")}
            </div>
          )}
          {selData.Dietary && (
            <div style={{
              display: "inline-block", marginTop: 8,
              padding: "2px 8px", borderRadius: 4,
              background: "var(--paper-2)", border: "1px solid var(--line)",
              fontSize: 10.5, color: "var(--ink-2)",
            }}>{selData.Dietary}</div>
          )}
        </div>
        <IconBtn icon={I.x} title="Close" onClick={onClose} />
      </div>

      {/* Per-activity placement summary */}
      <div style={{ padding: "16px 20px 6px" }}>
        <Eyebrow>Across the event</Eyebrow>
      </div>
      <div style={{ padding: "4px 20px 14px", display: "flex", flexDirection: "column", gap: 4 }}>
        {activities.map((a) => {
          const ActIcon = I[a.type] || I.other;
          const gr = a.groups.find(g => g.people.includes(selData.id));
          const isHost = gr?.host === selData.id;
          return (
            <div key={a.id} style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "6px 8px", borderRadius: 6,
              background: gr ? "var(--paper-0)" : "transparent",
              border: `1px solid ${gr ? "var(--line)" : "transparent"}`,
              opacity: gr ? 1 : 0.55,
            }}>
              <ActIcon size={12} stroke="var(--ink-3)" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {a.name}
                </div>
                <div style={{ fontSize: 11, color: "var(--ink-3)" }}>
                  {gr ? `${gr.name}${isHost ? " · hosting" : ""}` : "absent"}
                </div>
              </div>
              {isHost && <I.star size={11} stroke="var(--accent)" />}
            </div>
          );
        })}
      </div>

      <div style={{ padding: "14px 20px 4px", borderTop: "1px solid var(--line)" }}>
        <Eyebrow>Met across the event</Eyebrow>
        <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 6, lineHeight: 1.5 }}>
          People {firstName} will share a {label}, table or vehicle with — ordered by time spent together.
        </div>
      </div>
      <div style={{ padding: "8px 20px 14px", display: "flex", flexDirection: "column", gap: 2 }}>
        {connections.map(({ person, count, contexts }) => (
          <div
            key={person.id}
            onClick={() => onJump(person.id)}
            style={{
              padding: "9px 10px", borderRadius: 7,
              display: "flex", alignItems: "center", gap: 10,
              cursor: "pointer",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--paper-2)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {person.Name}
              </div>
              <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {contexts.slice(0, 2).map(c => c.activity).join(" · ")}
                {contexts.length > 2 && ` +${contexts.length - 2}`}
              </div>
            </div>
            <div style={{ display: "flex", gap: 2 }}>
              {[...Array(Math.min(count, maxBars))].map((_, i) => (
                <div key={i} style={{ width: 4, height: 14, borderRadius: 1, background: "var(--accent)" }} />
              ))}
            </div>
            <div style={{ fontSize: 12, color: "var(--ink-2)", fontVariantNumeric: "tabular-nums", width: 18, textAlign: "right" }}>
              {count}×
            </div>
          </div>
        ))}
        {connections.length === 0 && (
          <div style={{ fontSize: 12, color: "var(--ink-3)", fontStyle: "italic" }}>
            Not yet placed anywhere.
          </div>
        )}
      </div>

      <div style={{ flex: 1 }} />
      <div style={{ padding: "12px 20px 18px", borderTop: "1px solid var(--line)" }}>
        <Btn variant="danger" size="sm" onClick={onRemove}>Remove from event</Btn>
      </div>
    </div>
  );
}
